/**
 * @file priceChangeDates.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-07
 * @modified 2026-10-12
 * @description The effective-date rules for a queued price change, as the
 *              Price Changes screen shows them. Mirrors the server's
 *              utils/priceChangeSchedule.js (dateState) — keep the two in step.
 *
 *              "Today" is the server's London date (the list response carries
 *              it), falling back to London's date by this machine's clock, so
 *              an office PC with a skewed clock or zone does not decide what is
 *              overdue.
 *
 *                overdue   — dated today or earlier, not posted: bold, in the
 *                            error colour, "Due on <date> — not posted yet"
 *                scheduled — dated after today: muted, "Scheduled for <date>"
 *                posted / undated — shown plainly
 *
 *              1.2.0 (2026-10-12, Cam): a COST change dated before today is
 *              "backdated" — on posting, the person is asked which orders and
 *              POs delivered on or after its date (not yet on a customer
 *              invoice) take the new cost (`isBackdatedCost`,
 *              `backdatedNotice`), and a posted one can be reviewed again
 *              (`reviewBlock`).
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** London's calendar date now, as YYYY-MM-DD. */
export function londonToday(at = new Date()) {
  return new Date(at).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

/** YYYY-MM-DD → DD/MM/YYYY, without passing through a Date (no zone shift). */
export function formatEffectiveDate(value) {
  const s = value ? String(value).slice(0, 10) : '';
  if (!DATE_RE.test(s)) return '—';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
}

export function dateState(change, today = londonToday()) {
  if (!change) return 'undated';
  if (change.is_synced) return 'posted';
  const d = change.effective_date ? String(change.effective_date).slice(0, 10) : '';
  if (!DATE_RE.test(d)) return 'undated';
  return d <= today ? 'overdue' : 'scheduled';
}

export function isFutureDated(change, today = londonToday()) {
  return dateState(change, today) === 'scheduled';
}

/** The hover text for a change's date cell. */
export function dateTitle(change, today = londonToday()) {
  const shown = formatEffectiveDate(change?.effective_date);
  switch (dateState(change, today)) {
    case 'overdue':
      return change.auto_post
        ? `Due on ${shown} — not posted yet (auto-post runs at 05:30 each morning)`
        : `Due on ${shown} — not posted yet`;
    case 'scheduled':
      return change.auto_post
        ? `Scheduled for ${shown} — posts automatically at 05:30 that morning`
        : `Scheduled for ${shown} — post it by hand on the day`;
    case 'posted':
      return `Effective ${shown}`;
    default:
      return 'No date set';
  }
}

// When the worker's auto-post runs each morning, London time (worker.js).
export const AUTO_POST_TIME = '05:30';

/** London's wall-clock time now, as HH:MM. */
export function londonTime(at = new Date()) {
  return new Date(at).toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false
  });
}

/**
 * True when an auto-post change is already due but today's 05:30 run has been,
 * so the next run to pick it is TOMORROW morning — worth telling the person at
 * save time, while they could still post it now.
 */
export function postsTomorrow(change, { today = londonToday(), now = new Date() } = {}) {
  if (!change || change.is_synced || !change.auto_post) return false;
  if (dateState(change, today) !== 'overdue') return false;
  return londonTime(now) >= AUTO_POST_TIME;
}

export const POSTS_TOMORROW_TEXT =
  `Auto-post runs at ${AUTO_POST_TIME} each morning and has already run today, so this will post tomorrow morning.`;

/** The confirm text before posting changes ahead of their date. */
export function futurePostMessage(changes) {
  const list = changes || [];
  if (list.length === 1) {
    const c = list[0];
    return `${c.item_code || 'This change'} is dated ${formatEffectiveDate(c.effective_date)}, which is in the future. `
      + 'Post it to Xero now, ahead of its date?';
  }
  const dates = [...new Set(list.map((c) => formatEffectiveDate(c.effective_date)))];
  return `${list.length} of the selected changes are dated in the future (${dates.join(', ')}). `
    + 'Post them to Xero now, ahead of their dates?';
}

/** A queued COST change dated before today (London). */
export function isBackdatedCost(change, today = londonToday()) {
  if (!change || change.is_synced) return false;
  if (change.cost === null || change.cost === undefined || change.cost === '') return false;
  const d = change.effective_date ? String(change.effective_date).slice(0, 10) : '';
  return DATE_RE.test(d) && d < today;
}

/**
 * What to tell someone who has just saved or scheduled cost changes dated
 * before today. `dates` are YYYY-MM-DD; null when there is nothing to say.
 */
export function backdatedNotice(dates) {
  const list = [...new Set((dates || []).filter(Boolean).map((d) => String(d).slice(0, 10)))].sort();
  if (!list.length) return null;
  const shown = list.map(formatEffectiveDate).join(', ');
  return `${list.length === 1 ? `This cost is dated ${shown}` : `These costs are dated ${shown}`}, before today. `
    + 'When it is posted you will be asked which orders and POs delivered on or after that date — and not yet on a '
    + 'customer invoice — should take the new cost. If it posts itself at 05:30, nothing is changed and it is marked '
    + '"Orders to review" here instead.';
}

/**
 * Why "Review orders" is unavailable on a change, or null when it can be used.
 */
export function reviewBlock(change) {
  if (!change) return 'Nothing selected';
  if (!change.is_synced) return 'Post it first — the orders it affects are reviewed once it is posted';
  if (change.cost === null || change.cost === undefined || change.cost === '') {
    return 'A sales price only — no cost to carry to orders';
  }
  return null;
}
